import type { Empresa, PapelMembro } from '@entregas/core';
import type { Db } from './cliente';
import { ErroApi, exigir, exigirOuNulo } from './erros';
import { paraEmpresa } from './mapeadores';

export interface MinhaEmpresa {
  empresa: Empresa;
  papel: PapelMembro;
}

/** Loja do usuário logado (hoje: uma por conta). null = conta sem loja → onboarding. */
export async function obterMinhaEmpresa(db: Db): Promise<MinhaEmpresa | null> {
  const { data: sessao } = await db.auth.getUser();
  const userId = sessao.user?.id;
  if (!userId) throw new ErroApi('nao_autenticado');

  const linha = exigirOuNulo(
    await db
      .from('membros')
      .select('papel, empresas(*)')
      .eq('user_id', userId)
      .order('criado_em')
      .limit(1)
      .maybeSingle(),
  );
  if (!linha?.empresas) return null;
  return { empresa: paraEmpresa(linha.empresas), papel: linha.papel };
}

export async function criarEmpresa(db: Db, nome: string): Promise<Empresa> {
  return paraEmpresa(exigir(await db.rpc('criar_empresa', { p_nome: nome })));
}

/**
 * "Estou na loja" (app do dono) e "Usar a localização deste aparelho" (painel): marca onde a loja fica,
 * de onde a rota do entregador conta as distâncias. Só o dono; o banco carimba quando e recusa precisão
 * pior que PRECISAO_MAXIMA_DA_LOJA_M ou que o aparelho não informou (`local_impreciso`).
 */
export async function marcarLocalDaLoja(
  db: Db,
  local: { lat: number; lng: number; precisaoM: number },
): Promise<Empresa> {
  return paraEmpresa(
    exigir(
      await db.rpc('marcar_local_da_loja', {
        p_lat: local.lat,
        p_lng: local.lng,
        p_precisao: local.precisaoM,
      }),
    ),
  );
}

/**
 * Dados da loja (Configurações), inclusive a logo (data URL; null remove). Só o dono muda: para o balcão o RLS faz o update afetar 0 linhas, sem
 * erro, e isso vira `sem_permissao` (nunca sucesso silencioso).
 */
export async function atualizarEmpresa(
  db: Db,
  id: string,
  dados: Partial<
    Pick<
      Empresa,
      | 'nome'
      | 'telefone'
      | 'viradaDiaHora'
      | 'cidade'
      | 'uf'
      | 'taxaEntregaPadraoCentavos'
      | 'prazoMinutos'
      | 'entregaGratisAPartirCentavos'
      | 'logo'
    >
  >,
): Promise<Empresa> {
  const linha = exigirOuNulo(
    await db
      .from('empresas')
      .update({
        ...(dados.nome !== undefined && { nome: dados.nome }),
        ...(dados.telefone !== undefined && { telefone: dados.telefone }),
        ...(dados.viradaDiaHora !== undefined && { virada_dia_hora: dados.viradaDiaHora }),
        ...(dados.cidade !== undefined && { cidade: dados.cidade }),
        ...(dados.uf !== undefined && { uf: dados.uf }),
        ...(dados.taxaEntregaPadraoCentavos !== undefined && {
          taxa_entrega_padrao_centavos: dados.taxaEntregaPadraoCentavos,
        }),
        ...(dados.prazoMinutos !== undefined && { prazo_minutos: dados.prazoMinutos }),
        ...(dados.entregaGratisAPartirCentavos !== undefined && {
          entrega_gratis_a_partir_centavos: dados.entregaGratisAPartirCentavos,
        }),
        ...(dados.logo !== undefined && { logo: dados.logo }),
      })
      .eq('id', id)
      .select()
      .maybeSingle(),
  );
  if (!linha) throw new ErroApi('sem_permissao');
  return paraEmpresa(linha);
}
